import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { UserViewService } from '../core/view-states/user-view.service';
import { catchError, map, take } from 'rxjs/operators';
import { of } from 'rxjs';

/**
 * Type representing all available user feature flags in UserViewState
 */
type UserFeatureFlag =
  | 'promotions_enabled'
  | 'daily_claim_enabled'
  | 'purchases_fiat_enabled'
  | 'purchases_crypto_enabled'
  | 'redemptions_fiat_enabled'
  | 'redemptions_crypto_enabled';

/**
 * Type representing the (currently single) kill-switch-style feature flag: one
 * that defaults to enabled and is only ever explicitly turned OFF. See
 * {@link killSwitchGuard} for why this can't share {@link featureGuard}'s
 * opt-in semantics.
 */
type UserKillSwitchFeatureFlag = 'vip_enabled';

/**
 * Factory function that creates a route guard to check if a user feature is enabled.
 *
 * This guard performs two checks:
 * 1. Verifies the user is authenticated (has a UserViewState)
 * 2. Checks if the specified feature flag is explicitly enabled (true)
 *
 * If either check fails, the user is redirected to the specified URL.
 *
 * **Important**: This guard waits for user state to load. If used on initial
 * navigation, there may be a brief delay while the /self API call completes.
 *
 * @param featureKey - The key of the feature to check in UserViewState.
 *                     Must be a boolean feature flag (e.g., 'promotions_enabled')
 * @param redirectUrl - The URL to redirect to if authentication fails or
 *                      feature is not enabled. Defaults to '/' (home page)
 * @returns A CanActivateFn guard function
 *
 * @example
 * // Basic usage:
 * {
 *   path: 'promotions',
 *   canActivate: [featureGuard('promotions_enabled')],
 *   loadComponent: () => import('./promotions.component').then(c => c.PromotionsComponent)
 * }
 *
 * @example
 * // With custom redirect:
 * {
 *   path: 'daily-claim',
 *   canActivate: [featureGuard('daily_claim_enabled', '/games')],
 *   loadComponent: () => import('./daily-claim.component').then(c => c.DailyClaimComponent)
 * }
 *
 * @example
 * // Composing with AuthGuard for consistent auth handling:
 * {
 *   path: 'promotions',
 *   canActivate: [AuthGuard, featureGuard('promotions_enabled')],
 *   data: { authGuardPipe: redirectUnauthorizedToHome },
 *   loadComponent: () => import('./promotions.component').then(c => c.PromotionsComponent)
 * }
 */
export function featureGuard(featureKey: UserFeatureFlag, redirectUrl: string = '/'): CanActivateFn {
  return () => {
    const userView = inject(UserViewService);
    const router = inject(Router);

    return userView.state$.pipe(
      // Wait for the first user state emission from UserViewService
      // - For unauthenticated users: Immediately emits null (no API call)
      // - For authenticated users: Waits for /self API call to complete
      // Note: On initial navigation, there may be a brief delay for authenticated users
      take(1),
      map((user) => {
        // Handle unauthenticated users (null state)
        if (!user) {
          return router.createUrlTree([redirectUrl]);
        }

        // Check if the feature is explicitly enabled (must be true)
        // This handles null, undefined, and false cases
        const featureEnabled = user[featureKey];
        if (featureEnabled !== true) {
          return router.createUrlTree([redirectUrl]);
        }

        return true;
      }),
      // Handle any errors gracefully by redirecting
      catchError(() => {
        return of(router.createUrlTree([redirectUrl]));
      })
    );
  };
}

/**
 * Factory function that creates a route guard for a kill-switch-style feature
 * flag — one that defaults to ENABLED and is only ever explicitly turned off
 * (e.g. the VIP per-user kill-switch).
 *
 * This is the inverse of {@link featureGuard}'s semantics: `featureGuard` blocks
 * unless the flag is explicitly `true` (opt-in), which would lock out nearly
 * every user for a flag that is `null`/`undefined` until an admin has ever
 * toggled it. `killSwitchGuard` instead blocks ONLY when the flag is explicitly
 * `false`, matching the "absent user_features row = enabled" convention used
 * everywhere else this kill-switch is checked (e.g.
 * `vipFeature && !vipFeature.enabled` in the VIP claim services).
 *
 * Unauthenticated users are allowed through (the flag only applies to signed-in
 * VIP status) — callers that also need auth should compose with an auth guard.
 *
 * @param featureKey - The kill-switch flag to check in UserViewState.
 * @param redirectUrl - Where to redirect when the flag is explicitly disabled.
 */
export function killSwitchGuard(featureKey: UserKillSwitchFeatureFlag, redirectUrl = '/'): CanActivateFn {
  return () => {
    const userView = inject(UserViewService);
    const router = inject(Router);

    return userView.state$.pipe(
      take(1),
      map((user) => {
        // Unauthenticated: nothing to gate, let the route render as normal.
        if (!user) {
          return true;
        }

        // Only an explicit `false` blocks — null/undefined (never toggled) and
        // `true` both mean enabled.
        if (user[featureKey] === false) {
          return router.createUrlTree([redirectUrl]);
        }

        return true;
      }),
      // Fail open on error — a transient /self hiccup shouldn't lock a user out
      // of a feature that's enabled by default.
      catchError(() => of(true))
    );
  };
}
